import { AlbumContentSerialized } from 'networking/types/album-detail';
import { SharedAlbum } from 'networking/types/shared-album';
import { useContext, useMemo, useRef, useState } from 'react';
import { useScopedAlbums } from 'hooks/use-scoped-albums';
import { goToPage, RouteName } from 'routes';
import { withOrigin } from 'helpers/vault-breadcrumbs';
import {
  VaultDetailContext,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { AlbumContentType, VaultNavigationTabs } from 'common/enums';
import { AppContext } from 'context/app-context';
import { Menu, PositionType } from 'common/menu';
import { FilterButton } from 'common/filter-button';
import { DropDownListItem } from 'common/drop-down-list-item';
import { useClickOutside } from 'hooks/click-outside';
import { useIncrementalList } from 'hooks/use-incremental-list';
import { MemoryCard } from '../dashboard/components/memory-card';
import {
  filterMemoriesBySearch,
  filterMemoriesByType,
  flattenMemories,
  sortMemories,
} from '../utils/memory-list-utils';
import styles from './memories.module.scss';

const DEFAULT_CONTENT_FILTERS: Record<AlbumContentType, boolean> = {
  [AlbumContentType.video]: true,
  [AlbumContentType.audio]: true,
  [AlbumContentType.image]: true,
  [AlbumContentType.write]: true,
  [AlbumContentType.document]: true,
};

const CONTENT_TYPE_LABELS: Record<AlbumContentType, string> = {
  [AlbumContentType.video]: 'Videos',
  [AlbumContentType.audio]: 'Audios',
  [AlbumContentType.image]: 'Images',
  [AlbumContentType.write]: 'Notes',
  [AlbumContentType.document]: 'Documents',
};

// Every memory in the vault, flattened out of its albums. Mirrors the Albums
// tab's layout — content-type filter, grid — but the grid holds the
// dashboard's memory cards.
export const Memories = () => {
  const {
    state: { vaultId, albumCustomFilters, albumsSortBy, searchQuery },
    dispatch: dispatchVaultDetail,
  } = useContext(VaultDetailContext);
  // The vault's custom order, for AlbumsSortBy.custom.
  const memoryOrder = useContext(AppContext).state.currentVault?.memoryOrder;
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  useClickOutside(filterMenuRef, () => {
    if (showFilterMenu) setShowFilterMenu(false);
  });

  const contentFilters = albumCustomFilters ?? DEFAULT_CONTENT_FILTERS;
  const hasActiveContentFilter =
    albumCustomFilters !== null &&
    Object.values(albumCustomFilters).some((v) => !v);

  const handleContentFilterToggle = (contentType: AlbumContentType) => {
    const next = {
      ...contentFilters,
      [contentType]: !contentFilters[contentType],
    };
    const allChecked = Object.values(next).every(Boolean);
    dispatchVaultDetail({
      type: vaultDetailActions.UPDATE_ALBUM_CUSTOM_FILTERS,
      albumCustomFilters: allChecked ? null : next,
    });
  };

  // The "My Stuff" / "Shared With Me" switch (see useScopedAlbums): each album
  // with only the memories in that scope.
  const scoped = useScopedAlbums();
  const allAlbums = scoped.memoryAlbums;

  const allMemories = useMemo(
    () =>
      flattenMemories(allAlbums, (album) =>
        scoped.crossVault ? (album as SharedAlbum).sharedBy : undefined,
      ),
    [allAlbums, scoped.crossVault],
  );

  const memories = useMemo(() => {
    const list = filterMemoriesByType(
      filterMemoriesBySearch(allMemories, searchQuery),
      albumCustomFilters,
    );
    return sortMemories(list, albumsSortBy, memoryOrder);
  }, [allMemories, searchQuery, albumCustomFilters, albumsSortBy, memoryOrder]);

  // Reveal the grid in chunks as the user scrolls so a big vault doesn't
  // request every thumbnail at once.
  const {
    visibleItems: visibleMemories,
    hasMore,
    sentinelRef,
  } = useIncrementalList(memories);

  // Opening a memory goes to the vault-wide "Memories" view, focused on it —
  // the same destination the dashboard uses.
  const openMemory = (contentId: number) => {
    // A memory from another vault's shared album opens in that album.
    const sharedAlbum = scoped.crossVault
      ? (allAlbums.find((album) =>
          album.contents?.some(
            (content: AlbumContentSerialized) => content.id === contentId,
          ),
        ) as SharedAlbum | undefined)
      : undefined;
    if (sharedAlbum) {
      goToPage(
        RouteName.AlbumViewScreen,
        { id: sharedAlbum.vaultId, albumId: sharedAlbum.id },
        { content: contentId },
        withOrigin({ vaultId, tab: VaultNavigationTabs.memories }),
      );
      return;
    }
    goToPage(
      RouteName.MemoriesViewScreen,
      { id: vaultId },
      {
        content: contentId,
      },
      withOrigin({ vaultId, tab: VaultNavigationTabs.memories }),
    );
  };

  let emptyText =
    'No memories yet. Add entries to an album and they’ll show up here.';
  if (scoped.loading) emptyText = 'Loading shared memories…';
  else if (scoped.crossVault)
    emptyText = 'Nothing has been shared with you yet.';
  else if (scoped.othersOnly)
    emptyText = 'No one else has added memories to this vault yet.';
  else if (scoped.inOthersVault)
    emptyText = 'There are no memories in this vault yet.';

  return (
    <div className={styles.memoriesContainer}>
      <div className={styles.memoriesSection}>
        <div className={styles.sectionTitle}>
          <div className={styles.headerActions}>
            <div ref={filterMenuRef} className={styles.filterMenuWrap}>
              <FilterButton
                onClick={() => setShowFilterMenu((prev) => !prev)}
                isModalOpen={showFilterMenu}
                applyActiveFilterStyle={hasActiveContentFilter}
              />
              {showFilterMenu && (
                <Menu
                  position={PositionType.BottomLeft}
                  className={styles.filterSettingsMenu}
                >
                  <div className={styles.filterSettingsMenuHeader}>
                    <span className="text__body__semi__bold__small__gray800">
                      Filter by content type
                    </span>
                    <span className="text__body__regular__tiny__gray600">
                      Show only memories of the selected content types.
                    </span>
                  </div>
                  {Object.values(AlbumContentType).map((contentType) => (
                    <DropDownListItem
                      key={contentType}
                      checkboxItem={{
                        isChecked: contentFilters[contentType],
                        onChange: () => handleContentFilterToggle(contentType),
                        itemKey: contentType,
                      }}
                    >
                      {CONTENT_TYPE_LABELS[contentType]}
                    </DropDownListItem>
                  ))}
                </Menu>
              )}
            </div>
          </div>
        </div>
        {memories.length > 0 ? (
          <>
            <div className={styles.memoriesGrid}>
              {visibleMemories.map((memory) => (
                <MemoryCard
                  key={memory.id}
                  content={memory}
                  unsorted={memory.unsorted}
                  onClick={() => openMemory(memory.id)}
                />
              ))}
            </div>
            {hasMore && <div ref={sentinelRef} aria-hidden />}
          </>
        ) : (
          <p className={styles.empty}>
            {allMemories.length === 0 && emptyText}
            {allMemories.length > 0 && 'No memories match your filters.'}
          </p>
        )}
      </div>
    </div>
  );
};
